import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout/Layout';
import axios from 'axios';
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_BASE = "https://api.mbsmartweb.com/api";

const BotHistory = () => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentBot, setCurrentBot] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [searching, setSearching] = useState(false);
  const currentUserId = localStorage.getItem("UserId");

  // Fetch current user's bot status
  const fetchCurrentBotStatus = async () => {
    try {
      const response = await axios.post(`${API_BASE}/bot-status`, {
        Userid: currentUserId
      });
      
      if (response.data.success && response.data.hasActiveBot) {
        setCurrentBot(response.data.data);
      } else {
        setCurrentBot(null);
      }
    } catch (err) {
      console.error("Error fetching bot status:", err);
    }
  };

  // Fetch bot activation history
  const fetchBotHistory = async (userId = currentUserId) => {
    try {
      setLoading(true);
      const response = await axios.post(`${API_BASE}/bot-history`, {
        Userid: userId,
        limit: 50,
        offset: 0
      });
      
      if (response.data.success) {
        setHistory(response.data.data);
      } else {
        toast.error("Failed to load history");
      }
    } catch (err) {
      console.error("Error fetching history:", err);
      toast.error("Error loading bot activation history");
    } finally {
      setLoading(false);
    }
  };

  // Search another user's history
  const handleSearch = async () => {
    if (!selectedUserId.trim()) {
      toast.warning("Please enter a User ID");
      return;
    }

    try {
      setSearching(true);
      const response = await axios.post(`${API_BASE}/bot-history`, {
        Userid: selectedUserId,
        limit: 50,
        offset: 0
      });
      
      if (response.data.success) {
        setHistory(response.data.data);
        toast.success(`Showing history for user: ${selectedUserId}`);
      } else {
        toast.error("No history found for this user");
        setHistory([]);
      }
    } catch (err) {
      console.error("Error searching history:", err);
      toast.error("Error fetching user history");
      setHistory([]);
    } finally {
      setSearching(false);
    }
  };

  const handleReset = () => {
    setSelectedUserId('');
    fetchBotHistory(currentUserId);
  };

  useEffect(() => {
    if (currentUserId) {
      fetchCurrentBotStatus();
      fetchBotHistory();
    }
  }, [currentUserId]);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status badge
  const getStatusBadge = (status, endDate) => {
    if (status === 'ACTIVE') {
      const now = new Date();
      const expiry = new Date(endDate);
      if (expiry > now) {
        return <span className="badge badge-active">✓ Active</span>;
      } else {
        return <span className="badge badge-expired">✗ Expired</span>;
      }
    } else if (status === 'EXPIRED') {
      return <span className="badge badge-expired">✗ Expired</span>;
    }
    return <span className="badge badge-inactive">• Inactive</span>;
  };

  // Calculate days remaining
  const getDaysRemaining = (endDate) => {
    if (!endDate) return null;
    const now = new Date();
    const expiry = new Date(endDate);
    const diffTime = expiry - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return 'Expired';
    if (diffDays === 0) return 'Expires today';
    return `${diffDays} days remaining`;
  };

  return (
    <Layout>
      <ToastContainer 
        position="top-center" 
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      
      <div className="bot-history-page">
        <div className="page-header">
          <h1>Bot Activation History</h1>
          <p>View all your bot subscription records</p>
        </div>

        {/* Current Active Bot Card */}
        {currentBot && (
          <div className="current-bot-card">
            <div className="card-header">
              <i className="fa fa-robot"></i>
              <h2>Current Active Subscription</h2>
            </div>
            <div className="bot-details">
              <div className="detail-item">
                <span className="label">Bot ID:</span>
                <strong>{currentBot.botId}</strong>
              </div>
              <div className="detail-item">
                <span className="label">Package:</span>
                <strong>{currentBot.packageName || '1 Year Subscription'}</strong>
              </div>
              <div className="detail-item">
                <span className="label">Amount:</span>
                <strong>${currentBot.amount}</strong>
              </div>
              <div className="detail-item">
                <span className="label">Activated On:</span>
                <strong>{formatDate(currentBot.activatedDate)}</strong>
              </div>
              <div className="detail-item">
                <span className="label">Expires On:</span>
                <strong className="expiry-date">{formatDate(currentBot.endDate)}</strong>
              </div>
              <div className="detail-item">
                <span className="label">Days Remaining:</span>
                <strong className="days-remaining">{getDaysRemaining(currentBot.endDate)}</strong>
              </div>
            </div>
            <div className="progress-bar-container">
              <div 
                className="progress-bar" 
                style={{
                  width: `${Math.max(0, Math.min(100, 
                    ((new Date(currentBot.endDate) - new Date(currentBot.startDate)) - 
                     (new Date(currentBot.endDate) - new Date())) / 
                    (new Date(currentBot.endDate) - new Date(currentBot.startDate)) * 100
                  ))}%`
                }}
              ></div>
            </div>
          </div>
        )}

        {/* Search Section */}
        <div className="search-section">
          <div className="search-box">
            <input
              type="text"
              placeholder="Enter User ID to view their history"
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              className="search-input"
            />
            <button onClick={handleSearch} className="btn-search" disabled={searching}>
              {searching ? <i className="fa fa-spinner fa-spin"></i> : <i className="fa fa-search"></i>}
              Search
            </button>
            <button onClick={handleReset} className="btn-reset">
              <i className="fa fa-refresh"></i>
              Reset
            </button>
          </div>
        </div>

        {/* History Table */}
        <div className="history-table-container">
          <div className="table-header">
            <h3>Activation History</h3>
            <div className="stats">
              Total Records: <strong>{history.length}</strong>
            </div>
          </div>
          
          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading history...</p>
            </div>
          ) : history.length === 0 ? (
            <div className="empty-state">
              <i className="fa fa-inbox"></i>
              <p>No bot activation history found</p>
              <button className="btn-primary" onClick={() => window.location.href='/activate'}>
                Activate Bot Now
              </button>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="history-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>User ID</th>
                    <th>Amount</th>
                    <th>Package</th>
                    <th>Start Date</th>
                    <th>End Date</th>
                    <th>Status</th>
                    <th>Activated By</th>
                    <th>Activation Date</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((record) => (
                    <tr key={record.Id}>
                      <td>{record.Id}</td>
                      <td className="user-id">{record.Userid}</td>
                      <td>${record.Amount}</td>
                      <td>{record.PackageId === 1 ? '1 Year' : `Package ${record.PackageId}`}</td>
                      <td>{formatDate(record.StartDate)}</td>
                      <td>{formatDate(record.EndDate)}</td>
                      <td>{getStatusBadge(record.Status, record.EndDate)}</td>
                      <td>{record.ActivatedBy || 'System'}</td>
                      <td>{formatDate(record.ActivationDate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Summary Cards */}
        {history.length > 0 && (
          <div className="summary-cards">
            <div className="summary-card">
              <i className="fa fa-calendar-check"></i>
              <div className="summary-info">
                <h4>Total Activations</h4>
                <p>{history.length}</p>
              </div>
            </div>
            <div className="summary-card">
              <i className="fa fa-dollar-sign"></i>
              <div className="summary-info">
                <h4>Total Amount</h4>
                <p>${history.reduce((sum, record) => sum + record.Amount, 0).toLocaleString()}</p>
              </div>
            </div>
            <div className="summary-card">
              <i className="fa fa-clock"></i>
              <div className="summary-info">
                <h4>Last Activation</h4>
                <p>{history[0] ? formatDate(history[0].ActivationDate) : 'N/A'}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        .bot-history-page {
          max-width: 1400px;
          margin: 0 auto;
          padding: 20px;
          animation: fadeInUp 0.5s ease;
        }
        
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .page-header {
          text-align: center;
          margin-bottom: 40px;
        }
        
        .page-header h1 {
          font-size: 32px;
          font-weight: 800;
          background: linear-gradient(135deg, #667eea, #764ba2);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          margin-bottom: 10px;
        }
        
        .page-header p {
          color: #718096;
          font-size: 16px;
        }
        
        /* Current Bot Card */
        .current-bot-card {
          background: linear-gradient(135deg, #667eea, #764ba2);
          border-radius: 20px;
          padding: 30px;
          margin-bottom: 30px;
          color: white;
          box-shadow: 0 10px 30px rgba(102, 126, 234, 0.3);
        }
        
        .card-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 25px;
        }
        
        .card-header i {
          font-size: 32px;
        }
        
        .card-header h2 {
          font-size: 24px;
          margin: 0;
        }
        
        .bot-details {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 20px;
          margin-bottom: 25px;
        }
        
        .detail-item {
          display: flex;
          flex-direction: column;
          gap: 5px;
        }
        
        .detail-item .label {
          font-size: 12px;
          opacity: 0.8;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        
        .detail-item strong {
          font-size: 18px;
        }
        
        .detail-item .expiry-date {
          color: #ffd700;
        }
        
        .detail-item .days-remaining {
          color: #ffd700;
        }
        
        .progress-bar-container {
          background: rgba(255,255,255,0.2);
          border-radius: 10px;
          overflow: hidden;
          height: 8px;
        }
        
        .progress-bar {
          background: #ffd700;
          height: 100%;
          transition: width 0.3s ease;
        }
        
        /* Search Section */
        .search-section {
          background: white;
          border-radius: 15px;
          padding: 20px;
          margin-bottom: 30px;
          box-shadow: 0 2px 10px rgba(0,0,0,0.05);
        }
        
        .search-box {
          display: flex;
          gap: 15px;
          align-items: center;
        }
        
        .search-input {
          flex: 1;
          padding: 12px 15px;
          border: 2px solid #e2e8f0;
          border-radius: 10px;
          font-size: 14px;
          transition: all 0.2s;
        }
        
        .search-input:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
        }
        
        .btn-search, .btn-reset {
          padding: 12px 24px;
          border: none;
          border-radius: 10px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        
        .btn-search {
          background: linear-gradient(135deg, #667eea, #764ba2);
          color: white;
        }
        
        .btn-search:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(102, 126, 234, 0.3);
        }
        
        .btn-reset {
          background: #f7fafc;
          color: #4a5568;
          border: 1px solid #e2e8f0;
        }
        
        .btn-reset:hover {
          background: #edf2f7;
        }
        
        /* History Table */
        .history-table-container {
          background: white;
          border-radius: 20px;
          padding: 25px;
          box-shadow: 0 5px 20px rgba(0,0,0,0.08);
        }
        
        .table-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          padding-bottom: 15px;
          border-bottom: 2px solid #edf2f7;
        }
        
        .table-header h3 {
          font-size: 20px;
          font-weight: 700;
          color: #2d3748;
        }
        
        .stats {
          color: #718096;
        }
        
        .stats strong {
          color: #667eea;
          font-size: 18px;
        }
        
        .table-responsive {
          overflow-x: auto;
        }
        
        .history-table {
          width: 100%;
          border-collapse: collapse;
        }
        
        .history-table thead th {
          text-align: left;
          padding: 15px;
          background: #f7fafc;
          font-weight: 600;
          color: #4a5568;
          font-size: 13px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        
        .history-table tbody td {
          padding: 15px;
          border-bottom: 1px solid #edf2f7;
          color: #2d3748;
        }
        
        .history-table tbody tr:hover {
          background: #f7fafc;
        }
        
        .user-id {
          font-weight: 600;
          color: #667eea;
        }
        
        /* Badges */
        .badge {
          display: inline-block;
          padding: 5px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
        }
        
        .badge-active {
          background: #c6f6d5;
          color: #22543d;
        }
        
        .badge-expired {
          background: #fed7d7;
          color: #742a2a;
        }
        
        .badge-inactive {
          background: #e2e8f0;
          color: #4a5568;
        }
        
        /* Loading State */
        .loading-state {
          text-align: center;
          padding: 60px 20px;
        }
        
        .spinner {
          width: 50px;
          height: 50px;
          border: 3px solid #edf2f7;
          border-top-color: #667eea;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 20px;
        }
        
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        
        /* Empty State */
        .empty-state {
          text-align: center;
          padding: 60px 20px;
        }
        
        .empty-state i {
          font-size: 64px;
          color: #cbd5e0;
          margin-bottom: 20px;
        }
        
        .empty-state p {
          color: #718096;
          margin-bottom: 20px;
        }
        
        /* Summary Cards */
        .summary-cards {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 20px;
          margin-top: 30px;
        }
        
        .summary-card {
          background: white;
          border-radius: 15px;
          padding: 20px;
          display: flex;
          align-items: center;
          gap: 15px;
          box-shadow: 0 2px 10px rgba(0,0,0,0.05);
          transition: transform 0.2s;
        }
        
        .summary-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 5px 20px rgba(0,0,0,0.1);
        }
        
        .summary-card i {
          font-size: 40px;
          color: #667eea;
        }
        
        .summary-info h4 {
          font-size: 14px;
          color: #718096;
          margin-bottom: 5px;
        }
        
        .summary-info p {
          font-size: 24px;
          font-weight: 700;
          color: #2d3748;
          margin: 0;
        }
        
        /* Responsive */
        @media (max-width: 768px) {
          .search-box {
            flex-direction: column;
          }
          
          .btn-search, .btn-reset {
            width: 100%;
            justify-content: center;
          }
          
          .bot-details {
            grid-template-columns: 1fr;
          }
          
          .summary-cards {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </Layout>
  );
};

export default BotHistory;